import React, { useEffect } from 'react';
import { Loader2, TriangleAlert, CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccountDeletion } from '../hooks/useAccountDeletion';

const AccountDeletionPage: React.FC = () => {
  useEffect(() => { document.title = 'Solicitação de exclusão de conta'; }, []);
  const { confirmText, setConfirmText, reason, setReason, loading, error, success, handleDelete } =
    useAccountDeletion();

  const isConfirmed = confirmText === 'DELETAR';
  const hasTypedWrong = confirmText.length > 0 && !isConfirmed;

  return (
    <main
      className="space-y-4 px-4 py-6"
      id="main-content"
      role="main"
      aria-label="Conteúdo principal"
    >
      <div>
        <h1 className="text-xl font-bold text-slate-900">Excluir conta</h1>
        <p className="mt-1 text-sm text-slate-500">Solicitação de exclusão permanente de dados.</p>
      </div>

      <div
        role="alert"
        aria-live="polite"
        className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4"
      >
        <TriangleAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
        <p className="text-sm text-amber-800">
          Esta ação é <strong>irreversível</strong>. Todos os seus dados serão excluídos de acordo
          com a legislação vigente (LGPD/GDPR). Você perderá acesso à sua conta e não poderá
          recuperá-la.
        </p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <form
          aria-label="Formulário de exclusão de conta"
          autoComplete="off"
          onSubmit={e => { e.preventDefault(); handleDelete(); }}
          className="space-y-4"
        >
          <div>
            <label
              htmlFor="deletion-reason"
              className="mb-1.5 block text-xs font-medium text-slate-500"
            >
              Motivo (opcional)
            </label>
            <textarea
              id="deletion-reason"
              aria-label="Motivo (opcional)"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="Descreva o motivo da exclusão..."
              className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:ring-1 focus:ring-slate-400"
            />
          </div>

          <div>
            <label
              htmlFor="confirm-text"
              className="mb-1.5 block text-xs font-medium text-slate-500"
            >
              Digite DELETAR para confirmar
            </label>
            <input
              id="confirm-text"
              type="text"
              aria-label="Digite DELETAR para confirmar"
              aria-required="true"
              value={confirmText}
              onChange={e => setConfirmText(e.target.value)}
              placeholder="DELETAR"
              className="w-full rounded-lg border bg-white px-3 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:ring-1 border-slate-200 focus:border-red-400 focus:ring-red-400"
            />
            {hasTypedWrong && (
              <p className="mt-1 text-xs text-red-600">
                Digite exatamente DELETAR para confirmar.
              </p>
            )}
          </div>

          <Button
            type="submit"
            variant="destructive"
            className="w-full gap-2"
            disabled={!isConfirmed || loading}
            aria-label="Solicitar exclusão de conta"
            onClick={handleDelete}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            {loading ? 'Processando...' : 'Solicitar exclusão de conta'}
          </Button>
        </form>
      </div>

      {error && (
        <div
          role="alert"
          aria-live="assertive"
          className="flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
        >
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {success && (
        <div
          role="alert"
          aria-live="polite"
          className="flex gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
          <p className="text-sm text-green-800">
            Solicitação enviada com sucesso. Você será desconectado.
          </p>
        </div>
      )}
    </main>
  );
};

export default AccountDeletionPage;
